import * as React from 'react'
import {Box} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, WARNING, PRIMARY_BLUE} from '../../../../../styles/foundation/colors'
import {GAP, RADIUS} from '../../../../../styles/foundation/spacing'
import {round3} from '../../weightMath'
import {kg, int, pct} from '../../format'

/**
 * Weight Freeze (v2) — Packing: the charged-weight histogram for one SKU.
 *
 * Three columns per bucket: the weight, a bar proportional to that bucket's
 * share of the tallest bucket, and `count (percentage)`.
 *
 * Deliberately hand-rolled rather than drawn with Recharts, which is already
 * a dependency. A chart library renders the bars into an SVG canvas it owns,
 * and the accessibility contract this component carries — ONE `role="img"`
 * node whose `aria-label` reads the entire distribution aloud — is the whole
 * point of the component. The bars themselves carry no text; a screen-reader
 * user gets nothing at all unless the container narrates them. Rows are plain
 * DOM inside that node so the label is the single source of truth and cannot
 * drift from what is drawn.
 *
 * Colour mapping is load-bearing, not decorative: the modal bucket is the
 * primary blue and EVERY other bucket is the warning amber, because copy
 * elsewhere in Packing refers to "the amber bars" as the shipments that fell
 * outside the most common weight. Do not recolour one without the other.
 *
 * `hist` is an array of `[weightKg, count]` pairs, in any order — it is sorted
 * ascending here. `mpw` is the modal weight, supplied by the caller. Ties in
 * the source data break to the LOWER weight; that decision belongs to whoever
 * built the histogram, so `mpw` is never recomputed here.
 *
 * Renders nothing when there are no buckets. A SKU with no observed shipments
 * is a statement the surrounding card should make in prose (EmptyState), not
 * something an empty grid can say.
 */

/** '1 shipment' vs '2 shipments' — int() itself never pluralises. */
export function shipments(count) {
  return `${int(count)} shipment${count === 1 ? '' : 's'}`
}

export default function DistributionBars({hist, mpw}) {
  const rows = (hist || []).slice().sort((a, b) => a[0] - b[0])
  if (!rows.length) return null

  let total = 0
  let max = 0
  rows.forEach(([, count]) => {
    total += count
    if (count > max) max = count
  })

  // Weights arrive from chargeable-weight arithmetic, so compare at the same
  // precision weightMath settles on rather than trusting raw float equality —
  // otherwise a 1.2 that is really 1.2000000000000002 silently loses its
  // primary-colour highlight and the whole chart reads as "no modal weight".
  const modal = mpw == null ? null : round3(mpw)
  const share = (count) => (total ? (count / total) * 100 : 0)

  // The bars convey nothing on their own, so the container narrates the full
  // distribution — every weight, its count and its share, with the modal
  // bucket called out in words the way the colour calls it out visually.
  const label =
    `Charged weight distribution across ${shipments(total)}. ` +
    rows
      .map(([weight, count]) => {
        const isModal = modal !== null && round3(weight) === modal
        // share() is passed through untouched: pct renders anything strictly
        // between 0 and 1 as 'Under 1%', and rounding here first would turn a
        // real 0.4% bucket into a spoken '0%'.
        return `${kg(weight)}: ${shipments(count)}, ${pct(share(count))}${isModal ? ', most common' : ''}`
      })
      .join('. ') +
    '.'

  return (
    <Box role='img' aria-label={label} sx={{display: 'grid', gap: GAP.xs}}>
      {rows.map(([weight, count]) => {
        const isModal = modal !== null && round3(weight) === modal

        return (
          <Box
            key={weight}
            sx={{
              display: 'grid',
              gridTemplateColumns: '58px 1fr 96px',
              alignItems: 'center',
              columnGap: GAP.sm,
              fontFamily: FONT,
              fontSize: SIZE['2xs'],
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <Box component='span' sx={{color: SUBTLE, fontWeight: isModal ? WEIGHT.semibold : WEIGHT.regular}}>
              {kg(weight)}
            </Box>

            <Box
              component='span'
              sx={{
                height: 8,
                borderRadius: RADIUS.sm,
                bgcolor: BORDER,
                overflow: 'hidden',
              }}
            >
              {/* Scaled against the tallest bucket, not against the total, so a
                  distribution of many small buckets is still readable. The 2%
                  floor keeps a bucket of one shipment visible instead of
                  collapsing it to nothing. */}
              <Box
                component='span'
                sx={{
                  display: 'block',
                  height: '100%',
                  width: `${Math.max(2, (count / (max || 1)) * 100)}%`,
                  bgcolor: isModal ? PRIMARY_BLUE : WARNING,
                }}
              />
            </Box>

            <Box
              component='span'
              sx={{textAlign: 'right', color: NAVY, fontWeight: isModal ? WEIGHT.semibold : WEIGHT.regular}}
            >
              {int(count)}
              <Box component='span' sx={{color: SUBTLE, fontWeight: WEIGHT.regular}}>
                {` (${pct(share(count))})`}
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
